/** Credit-note print view: drafts use a watermarked preview; emitted notes use the saved PDF. */
import { useEffect, useMemo, useRef } from "react";
import { Link, createFileRoute } from "@tanstack/react-router";
import { ArrowLeft, Download, Loader2, Printer } from "lucide-react";
import { useTranslation } from "react-i18next";
import { RequireAuth } from "@/components/RequireAuth";
import { ErrorState } from "@/components/ErrorState";
import { Button } from "@/components/ui/button";
import {
  useSalesNotePrintPdf,
  useSalesNote,
} from "@/lib/api/sales-notes.queries";
import { notePdfAction } from "@/components/sales-notes/pdf-actions";
import { useActiveCompany } from "@/lib/company-context";

export const Route = createFileRoute(
  "/companies/$companyId/notas-credito/$noteId/preview",
)({
  component: CreditNotePreviewPage,
});

function CreditNotePreviewPage() {
  return (
    <RequireAuth>
      <PreviewContent />
    </RequireAuth>
  );
}

function PreviewContent() {
  const { t } = useTranslation();
  const { companyId, noteId } = Route.useParams();
  const { setActiveCompanyId } = useActiveCompany();
  const iframeRef = useRef<HTMLIFrameElement>(null);

  useEffect(() => {
    if (companyId) setActiveCompanyId(companyId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId]);

  const noteQuery = useSalesNote("credit", companyId, noteId);
  const status = noteQuery.data?.status;
  const action = status ? notePdfAction(status) : null;
  const pdfQuery = useSalesNotePrintPdf("credit", companyId, noteId, status);

  const pdfBlob = pdfQuery.data;
  const objectUrl = useMemo(
    () => (pdfBlob ? URL.createObjectURL(pdfBlob) : null),
    [pdfBlob],
  );
  useEffect(() => {
    return () => {
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [objectUrl]);

  const documentId = noteQuery.data?.documentId ?? noteId;
  const fileName = action === "download" ? `${documentId}.pdf` : `borrador-${documentId}.pdf`;

  function onPrint() {
    const frameWindow = iframeRef.current?.contentWindow;
    if (!frameWindow) return;
    frameWindow.focus();
    frameWindow.print();
  }

  function onDownload() {
    if (!objectUrl) return;
    const anchor = document.createElement("a");
    anchor.href = objectUrl;
    anchor.download = fileName;
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
  }

  return (
    <div className="flex h-screen flex-col gap-4 p-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-3">
          <Button asChild variant="outline" size="sm">
            <Link
              to="/companies/$companyId/notas-credito/$noteId"
              params={{ companyId, noteId }}
            >
              <ArrowLeft className="h-4 w-4" />
              {t("notes.preview.back")}
            </Link>
          </Button>
          <div>
            <h1 className="text-sm font-semibold">{t("notes.preview.title")}</h1>
            <p className="font-mono text-xs text-muted-foreground">{documentId}</p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={onDownload} disabled={!objectUrl}>
            <Download className="h-4 w-4" />
            {t("notes.preview.download")}
          </Button>
          <Button size="sm" onClick={onPrint} disabled={!objectUrl}>
            <Printer className="h-4 w-4" />
            {t("notes.preview.print")}
          </Button>
        </div>
      </div>

      {noteQuery.isPending || (action && pdfQuery.isPending) ? (
        <div className="flex flex-1 flex-col items-center justify-center gap-3">
          <Loader2 className="h-6 w-6 animate-spin text-primary" />
          <p className="text-sm text-muted-foreground">{t("notes.preview.loading")}</p>
        </div>
      ) : noteQuery.isError || pdfQuery.isError || !objectUrl ? (
        <div className="flex flex-1 items-center justify-center">
          <ErrorState
            title={t("notes.preview.error_title")}
            description={t("notes.preview.error_body")}
            error={noteQuery.error ?? pdfQuery.error}
            onRetry={() => { void noteQuery.refetch(); if (action) void pdfQuery.refetch(); }}
            retryLabel={t("common.retry")}
          />
        </div>
      ) : (
        <iframe
          ref={iframeRef}
          src={objectUrl}
          title={t("notes.preview.title")}
          className="w-full flex-1 rounded-lg border bg-muted"
        />
      )}
    </div>
  );
}
